import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { v4 } from 'uuid';
import { useChangeEffect } from '@utils/react';
import { makeComponentEntryEnvelopeFromComponentEnvelope } from '../util.make-component-entry-envelope.js';
import { useMutateEntry } from './hook.mutate-entry.js';
import { getEntriesForComponentQueryKey } from './query.get-all.js';

let resetCounter = 0;
function useEntryKey(args) {
  const queryClient = useQueryClient();
  const [entryKey, setEntryKey] = useState(() => args.defaultEntryKey ?? v4());
  const updateEntry = useMutateEntry(entryKey);
  useChangeEffect(() => {
    if (args.defaultEntryKey) {
      setEntryKey(args.defaultEntryKey);
    }
  }, [args.defaultEntryKey]);
  const onResetEntry = useCallback(initialState => {
    if (args.parentOnResetEntry) {
      const nextEntryKey = args.parentOnResetEntry?.(initialState);
      setEntryKey(nextEntryKey);
      args.onEntryKeyReset?.(nextEntryKey);
      return nextEntryKey;
    } else {
      const nextEntryKey = `new-entry.${args.componentEnvelope.ID}.${resetCounter++}`;
      const entryData = args.schema.parse(initialState ?? {});
      const nextEntry = updateEntry(previousEntry => {
        const nextEntry = {
          learningObject: previousEntry.learningObject,
          data: args.postprocessEntry?.(entryData) ?? entryData,
          entryKey: nextEntryKey,
          state: {
            result: "success",
            isMutating: false
          },
          envelope: makeComponentEntryEnvelopeFromComponentEnvelope({
            id: previousEntry?.envelope.userID ?? ""
          }, args.componentEnvelope)
        };

        //
        // STEP: Ensure that our collection of all entries are updated to
        // reflect that a new entry is about to be added
        //
        queryClient.setQueryData(getEntriesForComponentQueryKey(args.componentEnvelope.ID), prev => {
          const next = prev ?? [];
          const prevInArr = next?.find(it => it.envelope.objectID === previousEntry?.envelope.objectID);
          if (prevInArr) {
            prevInArr.data = JSON.parse(JSON.stringify(previousEntry.data));
            prevInArr.envelope = JSON.parse(JSON.stringify(previousEntry.envelope));
          } else {
            next.push({
              data: JSON.parse(JSON.stringify(previousEntry.data)),
              envelope: JSON.parse(JSON.stringify(previousEntry.envelope))
            });
          }
          next.push(nextEntry);
          return next;
        });
        return nextEntry;
      });
      setEntryKey(nextEntry.entryKey);
      args.onEntryKeyReset?.(nextEntry.entryKey);
      return nextEntry.entryKey;
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [args.componentEnvelope, args.schema, args.onEntryKeyReset, entryKey, updateEntry]);
  return [entryKey, onResetEntry];
}

export { useEntryKey };
//# sourceMappingURL=hook.entry-key.js.map
